import { Component, Input } from '@angular/core';

@Component({
  selector: 'app-dashboard-card',
  template: `
    <mat-card class="dashboard-card" [ngClass]="panelClass">
      <mat-card-header class="dashboard-card-header" [style.background]="headerBackground || null">
        <mat-card-title
          class="dashboard-card-title"
          [class.dashboard-card-title--draggable]="draggable"
          cdkDragHandle
          [cdkDragHandleDisabled]="!draggable">
          {{ title }}
        </mat-card-title>
        <div class="dashboard-card-actions">
          <button
            *ngIf="draggable"
            type="button"
            class="dashboard-drag-handle"
            cdkDragHandle
            [attr.aria-label]="'Drag ' + title">
            <mat-icon>drag_indicator</mat-icon>
          </button>
          <button
            mat-icon-button
            type="button"
            (click)="toggleCollapsed()"
            [attr.aria-label]="(isCollapsed ? 'Expand ' : 'Collapse ') + title">
            <mat-icon>{{ isCollapsed ? 'expand_more' : 'expand_less' }}</mat-icon>
          </button>
        </div>
      </mat-card-header>
      <mat-card-content *ngIf="!isCollapsed">
        <ng-content></ng-content>
      </mat-card-content>
    </mat-card>
  `,
  styles: [
    `
    :host {
      display: block;
      height: 100%;
    }

    .dashboard-card {
      height: 100%;
      display: flex;
      flex-direction: column;
      background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
      color: var(--mat-sys-on-surface, var(--sys-on-surface));
    }

    .dashboard-card {
      --mdc-filled-button-container-height: 28px;
      --mdc-outlined-button-container-height: 28px;
      --mdc-text-button-container-height: 28px;
      --mat-form-field-container-height: 34px;
      --mat-form-field-container-vertical-padding: 6px;
    }

    .dashboard-card .mdc-button:not(.mat-mdc-icon-button) {
      min-height: 28px;
      font-size: 11px;
      padding: 0 8px;
    }

    .dashboard-card .mat-mdc-icon-button.mat-mdc-button-base {
      width: 28px;
      height: 28px;
      min-width: 28px;
      min-height: 28px;
      padding: 2px;
    }

    .dashboard-card .mat-mdc-form-field {
      font-size: 11px;
    }

    .dashboard-card.dashboard-card--info {
      border-left: 4px solid var(--mat-sys-primary, var(--sys-primary));
      background: var(--status-info);
      color: var(--status-on-info);
    }

    .dashboard-card.dashboard-card--success {
      border-left: 4px solid var(--mat-sys-secondary, var(--sys-secondary));
      background: var(--status-success);
      color: var(--status-on-success);
    }

    .dashboard-card.dashboard-card--warning {
      border-left: 4px solid var(--mat-sys-tertiary, var(--sys-tertiary));
      background: var(--status-warning);
      color: var(--status-on-warning);
    }

    .dashboard-card.dashboard-card--danger {
      border-left: 4px solid var(--mat-sys-error, var(--sys-error));
      background: var(--status-danger);
      color: var(--status-on-danger);
    }

    .dashboard-card.dashboard-card--info .dashboard-card-header {
      background: var(--status-info);
      color: var(--status-on-info);
    }

    .dashboard-card.dashboard-card--success .dashboard-card-header {
      background: var(--status-success);
      color: var(--status-on-success);
    }

    .dashboard-card.dashboard-card--warning .dashboard-card-header {
      background: var(--status-warning);
      color: var(--status-on-warning);
    }

    .dashboard-card.dashboard-card--danger .dashboard-card-header {
      background: var(--status-danger);
      color: var(--status-on-danger);
    }

    .dashboard-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      padding-bottom: 4px;
      background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
      border-radius: 8px 8px 0 0;
      padding: 6px 8px;
      margin-bottom: 6px;
      border-bottom: 0;
      position: relative;
    }

    .dashboard-card-header::after {
      content: '';
      position: absolute;
      left: 8px;
      right: 8px;
      bottom: 0;
      height: 1px;
      background: var(--mat-sys-outline-variant, var(--sys-outline-variant));
      opacity: 0.28;
      pointer-events: none;
    }

    .dashboard-card-title {
      cursor: default;
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding-left: 4px;
    }

    .dashboard-card-title.dashboard-card-title--draggable {
      cursor: grab;
    }

    .dashboard-card-actions {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .dashboard-drag-handle {
      width: 24px;
      height: 24px;
      min-width: 24px;
      min-height: 24px;
      border: 0;
      border-radius: 50%;
      background: transparent;
      color: var(--mat-sys-on-surface-variant, var(--sys-on-surface-variant));
      cursor: grab;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      transition: opacity 120ms ease-in-out, background-color 120ms ease-in-out;
      opacity: 0.75;
    }

    .dashboard-drag-handle .mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    .dashboard-drag-handle:hover,
    .dashboard-drag-handle:focus-visible {
      opacity: 1;
      background: var(--mat-sys-surface-container-high, var(--sys-surface-container-high));
      outline: none;
    }

    .dashboard-card-header .mat-mdc-card-title {
      font-size: 16px;
      line-height: 22px;
      margin: 0;
    }

    .dashboard-card mat-card-content {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
      background: transparent;
    }

    .dashboard-card .mat-mdc-form-field .mat-mdc-text-field-wrapper {
      min-height: 34px;
    }

    .dashboard-card .mat-mdc-form-field .mat-mdc-form-field-infix {
      min-height: 34px;
      padding-top: 6px;
      padding-bottom: 6px;
    }

    .dashboard-card .mat-mdc-button.mat-primary,
    .dashboard-card .mat-mdc-outlined-button.mat-primary,
    .dashboard-card .mat-mdc-unelevated-button.mat-primary,
    .dashboard-card .mat-mdc-text-button.mat-primary {
      color: var(--mat-sys-on-surface, var(--sys-on-surface));
    }

    .dashboard-card ::ng-deep .mat-button-toggle-group {
      border-color: var(--mat-sys-outline-variant, var(--sys-outline-variant));
    }

    .dashboard-card ::ng-deep .mat-button-toggle {
      color: var(--mat-sys-on-surface-variant, var(--sys-on-surface-variant));
      background: var(--mat-sys-surface-container, var(--sys-surface-container));
    }

    .dashboard-card ::ng-deep .mat-button-toggle.mat-button-toggle-checked {
      background: var(--mat-sys-primary-container, var(--sys-primary-container));
      color: var(--mat-sys-on-primary-container, var(--sys-on-primary-container));
    }
    `
  ]
})
export class DashboardCardComponent {
  @Input() title = '';
  @Input() panelClass = '';
  @Input() headerBackground = '';
  @Input() draggable = true;
  isCollapsed = false;

  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }
}
